import React, { useMemo, useState } from "react";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";

import {
  DATE_RANGE_OPTIONS,
  DEFAULT_PAGE_NUMBER,
  DEFAULT_PAGE_SIZE,
  DEFAULT_UPLOAD_DATE_RANGE_ID,
  FILE_UPLOAD_STATUS,
  FILTER_ALL_VALUE,
  PAGE_SIZE_OPTIONS,
  UPLOAD_STATUS_FILTER_OPTIONS,
} from "constants/constants";
import { formatApiErrorForDisplay } from "helpers/packageManagementApi";
import Loader from "shared/Loader/Loader";
import { useFetchHistory, useFetchProcessedFile } from "store/backend/apiHooks";
import tokenUtils from "utils/tokenUtils";
import { useSnackbarContext } from "widgets/SnackBar/SnackBar";

import styles from "./FileUploadsHistory.module.scss";

const getRawDate = (row) =>
  row.created_at ?? row.createdAt ?? row.uploaded_at ?? null;

const formatUploadDate = (dateValue) => {
  if (!dateValue) {
    return "-";
  }

  const date = new Date(dateValue);

  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const getStatusVariant = (row) => {
  const statusId = row.status?.id;
  const errorRows = Number(row.result?.error_rows ?? 0);
  const totalRows = Number(row.result?.total_rows ?? 0);

  if (statusId === FILE_UPLOAD_STATUS.PROCESSED) {
    if (totalRows > 0 && errorRows >= totalRows) {
      return "failed";
    }

    if (errorRows > 0 && errorRows < totalRows) {
      return "partial";
    }

    return "processed";
  }

  if (statusId === FILE_UPLOAD_STATUS.UPLOADED) {
    return "uploaded";
  }

  return "pending";
};

const getStatusLabel = (row, variant) => {
  if (variant === "failed") {
    return "Failed";
  }

  if (variant === "partial") {
    return "Partial Success";
  }

  return row.status?.name || "Pending";
};

const isWithinDateRange = (row, dateRangeDays) => {
  if (dateRangeDays === null) {
    return true;
  }

  const rawDate = getRawDate(row);

  if (!rawDate) {
    return false;
  }

  const date = new Date(rawDate);

  if (Number.isNaN(date.getTime())) {
    return false;
  }

  const cutoff = new Date();
  cutoff.setHours(0, 0, 0, 0);
  cutoff.setDate(cutoff.getDate() - dateRangeDays);

  return date >= cutoff;
};

const handleOnClickDownloadProcessedFile = (
  fileId,
  fetchProcessedFile,
  openSnackbar
) => {
  if (!fileId) {
    return;
  }

  fetchProcessedFile(fileId)
    .then((response) => {
      const link = document.createElement("a");
      link.href = response.signedUrl;
      link.click();
      openSnackbar("Processed file downloaded successfully!", "success");
    })
    .catch((error) => {
      console.error("Error fetching processed file", error);
      openSnackbar(error.message || "Error fetching processed file", "error");
    });
};

const FileUploadsHistory = ({ scriptId }) => {
  const userId = tokenUtils.getUploaderUserId();
  const { openSnackbar } = useSnackbarContext();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState(FILTER_ALL_VALUE);
  const [dateRange, setDateRange] = useState(DEFAULT_UPLOAD_DATE_RANGE_ID);
  const [page, setPage] = useState(DEFAULT_PAGE_NUMBER);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const selectedDateRange =
    DATE_RANGE_OPTIONS.find((option) => option.id === dateRange) ||
    DATE_RANGE_OPTIONS[1];

  const {
    isPending: isHistoryLoading,
    data: history,
    isError,
    error,
  } = useFetchHistory(userId);

  const { fetchProcessedFile, isLoading: isFetchProcessedFileLoading } =
    useFetchProcessedFile();

  const filteredRows = useMemo(() => {
    if (!Array.isArray(history)) {
      return [];
    }

    return history
      .filter((row) => Number(row?.script?.id) === Number(scriptId))
      .filter((row) => isWithinDateRange(row, selectedDateRange.days))
      .filter((row) => {
        if (!searchTerm.trim()) {
          return true;
        }

        const query = searchTerm.trim().toLowerCase();
        const fileName = (row.name || "").toLowerCase();
        const statusName = (row.status?.name || "").toLowerCase();

        return fileName.includes(query) || statusName.includes(query);
      })
      .filter((row) => {
        if (statusFilter === FILTER_ALL_VALUE) {
          return true;
        }

        const variant = getStatusVariant(row);

        if (statusFilter === "processed") {
          return variant === "processed";
        }

        if (statusFilter === "failed") {
          return variant === "failed" || variant === "partial";
        }

        if (statusFilter === "uploaded") {
          return variant === "uploaded";
        }

        return false;
      })
      .sort((a, b) => new Date(getRawDate(b)) - new Date(getRawDate(a)));
  }, [history, scriptId, searchTerm, statusFilter, dateRange, selectedDateRange.days]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paginatedRows = filteredRows.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const startIndex = filteredRows.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endIndex = Math.min(currentPage * pageSize, filteredRows.length);

  const showToolbar = Boolean(userId);

  const renderContent = () => {
    if (!userId) {
      return (
        <div className={styles.statePanel}>
          <p className={styles.message}>
            Unable to load upload history. Please log in again.
          </p>
        </div>
      );
    }

    if (isHistoryLoading) {
      return (
        <div className={styles.statePanel}>
          <Loader />
        </div>
      );
    }

    if (isError) {
      return (
        <div className={styles.statePanel}>
          <p className={styles.errorMessage}>
            {formatApiErrorForDisplay(error, "Failed to load upload history.")}
          </p>
        </div>
      );
    }

    if (filteredRows.length === 0) {
      return (
        <div className={styles.statePanel}>
          <p className={styles.message}>
            No uploads match your filters. Try changing search, status, or date range.
          </p>
        </div>
      );
    }

    return (
      <>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>File Name</th>
                <th>Uploaded On</th>
                <th>Total Rows</th>
                <th>Error Rows</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedRows.map((row) => {
                const variant = getStatusVariant(row);
                const errorRows = Number(row.result?.error_rows ?? 0);
                const hasErrors = errorRows > 0;

                return (
                  <tr key={row.file_id || row.name}>
                    <td>
                      <div className={styles.fileCell}>
                        <InsertDriveFileOutlinedIcon className={styles.csvIcon} />
                        <span>{row.name || "-"}</span>
                      </div>
                    </td>
                    <td>{formatUploadDate(getRawDate(row))}</td>
                    <td>{row.result?.total_rows ?? "-"}</td>
                    <td className={hasErrors ? styles.errorCell : undefined}>
                      {row.result?.error_rows ?? "-"}
                    </td>
                    <td>
                      <div className={styles.statusCell}>
                        <span className={`${styles.statusPill} ${styles[variant]}`}>
                          {getStatusLabel(row, variant)}
                        </span>
                        {variant === "processed" && (
                          <span className={styles.statusSubtext}>Completed</span>
                        )}
                        {variant === "failed" && hasErrors && (
                          <span className={styles.statusSubtext}>
                            All rows failed to process
                          </span>
                        )}
                        {variant === "partial" && hasErrors && (
                          <span className={styles.statusSubtext}>
                            {errorRows} row(s) could not be processed
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className={styles.actionCell}>
                        {row.status?.id === FILE_UPLOAD_STATUS.PROCESSED ? (
                          <button
                            type="button"
                            className={styles.downloadBtn}
                            onClick={() =>
                              handleOnClickDownloadProcessedFile(
                                row.file_id,
                                fetchProcessedFile,
                                openSnackbar
                              )
                            }
                            disabled={isFetchProcessedFileLoading}
                          >
                            {isFetchProcessedFileLoading ? "Loading..." : "Download"}
                          </button>
                        ) : (
                          <button type="button" className={styles.downloadBtn} disabled>
                            Not Available
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className={styles.pagination}>
          <span className={styles.pageInfo}>
            Showing {startIndex} to {endIndex} of {filteredRows.length} uploads
          </span>

          <div className={styles.pageControls}>
            <button
              type="button"
              className={styles.pageBtn}
              disabled={currentPage <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Previous
            </button>

            {Array.from({ length: Math.min(totalPages, 5) }, (_, index) => {
              const pageNumber = index + 1;
              return (
                <button
                  key={pageNumber}
                  type="button"
                  className={`${styles.pageBtn} ${
                    pageNumber === currentPage ? styles.pageBtnActive : ""
                  }`}
                  onClick={() => setPage(pageNumber)}
                >
                  {pageNumber}
                </button>
              );
            })}

            <button
              type="button"
              className={styles.pageBtn}
              disabled={currentPage >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>

            <select
              className={styles.pageSizeSelect}
              value={pageSize}
              onChange={(event) => {
                setPageSize(Number(event.target.value));
                setPage(DEFAULT_PAGE_NUMBER);
              }}
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size} / page
                </option>
              ))}
            </select>
          </div>
        </div>
      </>
    );
  };

  return (
    <div className={styles.wrapper}>
      {showToolbar && (
        <div className={styles.toolbar}>
          <h2 className={styles.title}>Recent Uploads</h2>

          <div className={styles.filters}>
            <div className={styles.searchBox}>
              <SearchOutlinedIcon className={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search files..."
                value={searchTerm}
                onChange={(event) => {
                  setSearchTerm(event.target.value);
                  setPage(DEFAULT_PAGE_NUMBER);
                }}
                className={styles.searchInput}
              />
            </div>

            <select
              className={styles.filterSelect}
              value={statusFilter}
              onChange={(event) => {
                setStatusFilter(event.target.value);
                setPage(DEFAULT_PAGE_NUMBER);
              }}
            >
              {UPLOAD_STATUS_FILTER_OPTIONS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>

            <select
              className={styles.filterSelect}
              value={dateRange}
              onChange={(event) => {
                setDateRange(event.target.value);
                setPage(DEFAULT_PAGE_NUMBER);
              }}
            >
              {DATE_RANGE_OPTIONS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      <div className={styles.contentPanel}>{renderContent()}</div>
    </div>
  );
};

export default FileUploadsHistory;
